import { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import type { CoordinationExternalUser } from '../../lib/careCoordination';
import { useI18n } from '../../i18n/LanguageContext';

type ExternalUsersResponse = {
  data: CoordinationExternalUser[];
};

type EditableExternalUser = {
  id?: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  notes: string;
  active: boolean;
};

function emptyExternalUser(): EditableExternalUser {
  return {
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    active: true,
  };
}

const externalUsersItOverrides: Record<string, string> = {
  Back: 'Indietro',
  'Care Coordination': 'Coordinamento assistenziale',
  'External Users': 'Utenti esterni',
  'Reusable external assignees for care-plan tasks and automated medical forms.':
    'Assegnatari esterni riutilizzabili per attività del piano di cura e moduli medici automatizzati.',
  'Add External User': 'Aggiungi utente esterno',
  'Loading external users...': 'Caricamento utenti esterni...',
  'No external users configured.': 'Nessun utente esterno configurato.',
};

function createExternalUsersTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = externalUsersItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

export default function SettingsExternalUsers() {
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const { language, tr } = useI18n();
  const te = createExternalUsersTranslator(language, tr);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EditableExternalUser[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchJson<ExternalUsersResponse>('/care-coordination/settings/external-users')
      .then((response) => {
        if (!active) return;
        setRows(
          (response.data || []).map((item) => ({
            id: item.id,
            name: item.name,
            email: item.email || '',
            phone: item.phone || '',
            company: item.company || '',
            notes: item.notes || '',
            active: item.active,
          })),
        );
      })
      .catch((err: any) => {
        if (!active) return;
        setError(err?.message || 'Unable to load external users.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [fetchJson]);

  function patchRow(index: number, patch: Partial<EditableExternalUser>) {
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows((prev) => [...prev, emptyExternalUser()]);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  }

  async function saveRows() {
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const payload = rows
        .map((row) => ({
          id: row.id,
          name: row.name.trim(),
          email: row.email.trim() || null,
          phone: row.phone.trim() || null,
          company: row.company.trim() || null,
          notes: row.notes.trim() || null,
          active: row.active,
        }))
        .filter((row) => row.name);

      const response = await fetchJson<ExternalUsersResponse>('/care-coordination/settings/external-users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users: payload }),
      });

      setRows(
        response.data.map((item) => ({
          id: item.id,
          name: item.name,
          email: item.email || '',
          phone: item.phone || '',
          company: item.company || '',
          notes: item.notes || '',
          active: item.active,
        })),
      );
      setBanner('External users updated.');
    } catch (err: any) {
      setError(err?.message || 'Unable to save external users.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft size={14} />
            {te('Back')}
          </button>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{te('Care Coordination')}</p>
            <h1 className="text-2xl font-semibold text-ink">{te('External Users')}</h1>
            <p className="text-sm text-slate-500">
              {te('Reusable external assignees for care-plan tasks and automated medical forms.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Plus size={14} />
            {te('Add External User')}
          </button>
          <button
            type="button"
            onClick={saveRows}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60"
          >
            <Save size={14} />
            Save
          </button>
        </div>
      </div>

      {banner ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}
      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-2">Company</div>
          <div className="col-span-2">Notes</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading ? <div className="px-4 py-6 text-sm text-slate-500">{te('Loading external users...')}</div> : null}

        {!loading && rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-slate-500">{te('No external users configured.')}</div>
        ) : null}

        {!loading
          ? rows.map((row, index) => (
              <div key={row.id || `new-${index}`} className="grid grid-cols-12 gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
                <div className="col-span-3">
                  <input
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={row.name}
                    placeholder="Full name"
                    onChange={(event) => patchRow(index, { name: event.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={row.phone}
                    placeholder="Phone"
                    onChange={(event) => patchRow(index, { phone: event.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={row.email}
                    placeholder="Email"
                    onChange={(event) => patchRow(index, { email: event.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={row.company}
                    placeholder="Company"
                    onChange={(event) => patchRow(index, { company: event.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={row.notes}
                    placeholder="Notes"
                    onChange={(event) => patchRow(index, { notes: event.target.value })}
                  />
                </div>
                <div className="col-span-1 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-2 text-sm text-rose-700 hover:bg-rose-50"
                    aria-label={`Remove ${row.name || 'external user'}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}
